'use client';
import { useEffect, useState } from 'react';
import type { MouseEvent } from 'react';
import { createPortal } from 'react-dom';
import Close from '@mui/icons-material/Close';
import { NavbarSearchBar } from '@practium/ui';
import type { FloraLibraryDocument, FloraLibraryFilter } from '../../types/flora-ai.types';
import { FloraToggleButton } from '../FloraToggleButton/FloraToggleButton';
import { FloraLibraryUploadCard } from './FloraLibraryUploadCard';
import { FloraLibraryDocumentCard } from './FloraLibraryDocumentCard';
import styles from './FloraLibraryModal.module.css';

interface FloraLibraryModalProps {
	isOpen: boolean;
	onClose: () => void;
	filters: FloraLibraryFilter[];
	activeFilterId: string;
	onFilterChange: (filterId: string) => void;
	documents: FloraLibraryDocument[];
	onToggleDossier: (documentId: string) => void;
	selectedIds: string[];
	onToggleSelect: (documentId: string) => void;
	onSearch?: (query: string) => void;
	onUpload?: () => void;
	selectedCount: number;
	onRemoveSelected?: () => void;
	onAddSelected?: () => void;
}

export const FloraLibraryModal = ({
	isOpen,
	onClose,
	filters,
	activeFilterId,
	onFilterChange,
	documents,
	onToggleDossier,
	selectedIds,
	onToggleSelect,
	onSearch,
	onUpload,
	selectedCount,
	onRemoveSelected,
	onAddSelected,
}: FloraLibraryModalProps) => {
	const [searchQuery, setSearchQuery] = useState('');

	useEffect(() => {
		if (!isOpen) return;

		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === 'Escape') onClose();
		};

		document.addEventListener('keydown', handleKeyDown);
		return () => document.removeEventListener('keydown', handleKeyDown);
	}, [isOpen, onClose]);

	if (!isOpen) return null;

	const hasSelection = selectedCount > 0;

	const visibleDocuments = documents.filter((document) => {
		const matchesFilter = activeFilterId === 'all' || document.kind === activeFilterId;
		const matchesSearch = document.name.toLowerCase().includes(searchQuery.trim().toLowerCase());
		return matchesFilter && matchesSearch;
	});

	const handleSearch = (query: string) => {
		setSearchQuery(query);
		onSearch?.(query);
	};

	const handleOverlayClick = (e: MouseEvent<HTMLDivElement>) => {
		if (e.target === e.currentTarget) onClose();
	};

	return createPortal(
		<div className={styles.overlay} onClick={handleOverlayClick} role="presentation">
			<div className={styles.modal} role="dialog" aria-modal="true" aria-label="Biblioteca de documentos">
				<div className={styles.header}>
					<h2 className={styles.title}>Biblioteca</h2>

					<NavbarSearchBar onSearch={handleSearch} className={styles.search} />

					<div className={styles.filters}>
						{filters.map((filter) => (
							<FloraToggleButton
								key={filter.id}
								active={filter.id === activeFilterId}
								onClick={() => onFilterChange(filter.id)}
							>
								{filter.label}
							</FloraToggleButton>
						))}
					</div>

					<button
						type="button"
						className={styles.closeButton}
						onClick={onClose}
						aria-label="Fechar biblioteca"
					>
						<Close className={styles.closeIcon} />
					</button>
				</div>

				<div className={styles.content}>
					<FloraLibraryUploadCard onClick={onUpload} />

					{visibleDocuments.length === 0 ? (
						<div className={styles.emptyState}>
							<p className={styles.emptyTitle}>Nenhum documento encontrado</p>
							<p className={styles.emptySubtitle}>Troque o filtro ou tente outro nome</p>
						</div>
					) : (
						visibleDocuments.map((document) => (
							<FloraLibraryDocumentCard
								key={document.id}
								document={document}
								isSelected={selectedIds.includes(document.id)}
								onToggleSelect={onToggleSelect}
								onToggleDossier={onToggleDossier}
							/>
						))
					)}
				</div>

				<div className={styles.footer}>
					<span className={styles.footerText}>
						{hasSelection ? `${selectedCount} selecionado(s)` : 'Clique em um card para selecionar em lote'}
					</span>

					<div className={styles.footerActions}>
						<FloraToggleButton size="lg" onClick={onRemoveSelected} disabled={!hasSelection}>
							Excluir
						</FloraToggleButton>
						<button
							type="button"
							className={styles.addButton}
							onClick={onAddSelected}
							disabled={!hasSelection}
						>
							Adicionar ao dossiê
						</button>
					</div>
				</div>
			</div>
		</div>,
		document.body,
	);
};
